/**
 * Match a HangarMax catalog entry to a PlaneMAX `aircraft_types` row.
 *
 * What the source actually gives us, and why the tiers look like this:
 *
 *  - The scene references catalog entries by UUID. The CDN manifest resolves a
 *    UUID to a display name and dimensions — nothing else. There is no
 *    `asset_code`, no manufacturer and no ICAO type in the manifest, and the
 *    embedded `item_cache` has dimensions only.
 *  - HangarMax derives `asset_code` from `display_name` by kebab-casing it
 *    (2378 of 2420 aircraft rows match exactly), and PlaneMAX `type_code` uses
 *    the same kebab convention for the shared catalog ancestry. So kebab(name)
 *    is an effective stand-in for asset_code ↔ type_code.
 *  - Model hashes are NOT comparable: the same airframe is `32cb5018` in
 *    HangarMax and `90fbcd20` in PlaneMAX. Hash matching would silently fail,
 *    so it is deliberately not a tier.
 *
 * PlaneMAX carries ~141 aircraft types against HangarMax's ~2 400, so most
 * imports legitimately end with `method: null` — the caller still creates the
 * aircraft, using the HangarMax dimensions, and it renders procedurally.
 */

import type { AircraftMatch, AircraftTypeCandidate, HmCatalogEntry } from './types'

/** Wingspan/length tolerance for the dimensional tier. */
const DIMS_TOLERANCE = 0.03

const NO_MATCH: AircraftMatch = {
  method: null,
  aircraftTypeId: null,
  confidence: 0,
  typeLabel: null,
}

export function kebab(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function tokens(value: string): string[] {
  return kebab(value).split('-').filter(Boolean)
}

function typeLabel(candidate: AircraftTypeCandidate): string {
  const parts = [candidate.manufacturer, candidate.model].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : candidate.typeCode
}

/** Index built once per import run and reused for every aircraft item. */
export interface AircraftTypeIndex {
  byTypeCode: Map<string, AircraftTypeCandidate>
  byIcao: Map<string, AircraftTypeCandidate>
  byName: Map<string, AircraftTypeCandidate>
  all: AircraftTypeCandidate[]
}

export function buildAircraftTypeIndex(
  candidates: AircraftTypeCandidate[]
): AircraftTypeIndex {
  const byTypeCode = new Map<string, AircraftTypeCandidate>()
  const byIcao = new Map<string, AircraftTypeCandidate>()
  const byName = new Map<string, AircraftTypeCandidate>()

  for (const candidate of candidates) {
    const code = kebab(candidate.typeCode ?? '')
    if (code && !byTypeCode.has(code)) byTypeCode.set(code, candidate)

    const icao = candidate.icaoDesignator?.trim().toUpperCase()
    if (icao && !byIcao.has(icao)) byIcao.set(icao, candidate)

    const name = kebab(`${candidate.manufacturer ?? ''} ${candidate.model ?? ''}`)
    if (name && !byName.has(name)) byName.set(name, candidate)
  }

  return { byTypeCode, byIcao, byName, all: candidates }
}

export function matchAircraftType(
  entry: HmCatalogEntry | undefined,
  /** `Item.label` — sometimes carries a tail number or a type name. */
  itemLabel: string | null,
  index: AircraftTypeIndex
): AircraftMatch {
  const name = entry?.name?.trim() || null

  // Tier 1 — kebab(name) === type_code.
  if (name) {
    const slug = kebab(name)
    const exact = index.byTypeCode.get(slug)
    if (exact) {
      return {
        method: 'type_code',
        aircraftTypeId: exact.id,
        confidence: 1,
        typeLabel: typeLabel(exact),
      }
    }

    // Tier 2 — kebab(name) === kebab(manufacturer + model).
    const byName = index.byName.get(slug)
    if (byName) {
      return {
        method: 'type_code',
        aircraftTypeId: byName.id,
        confidence: 0.95,
        typeLabel: typeLabel(byName),
      }
    }
  }

  // Tier 3 — an ICAO designator appearing in the item label (e.g. "N123AB C172").
  if (itemLabel) {
    for (const token of itemLabel.toUpperCase().split(/[^A-Z0-9]+/)) {
      if (token.length < 3 || token.length > 4) continue
      const icaoHit = index.byIcao.get(token)
      if (icaoHit) {
        return {
          method: 'icao',
          aircraftTypeId: icaoHit.id,
          confidence: 0.9,
          typeLabel: typeLabel(icaoHit),
        }
      }
    }
  }

  // Tier 4 — token-overlap fuzzy match on the name.
  if (name) {
    const fuzzy = bestTokenMatch(name, index)
    if (fuzzy) return fuzzy
  }

  // Tier 5 — dimensions within tolerance on BOTH wingspan and length.
  if (entry?.widthM != null && entry?.lengthM != null) {
    const dims = bestDimensionMatch(entry, index)
    if (dims) return dims
  }

  return NO_MATCH
}

function bestTokenMatch(name: string, index: AircraftTypeIndex): AircraftMatch | null {
  const wanted = tokens(name)
  if (wanted.length === 0) return null

  let best: AircraftTypeCandidate | null = null
  let bestScore = 0

  for (const candidate of index.all) {
    const candidateTokens = new Set(
      tokens(`${candidate.manufacturer ?? ''} ${candidate.model ?? ''} ${candidate.typeCode}`)
    )
    if (candidateTokens.size === 0) continue

    let hits = 0
    for (const token of wanted) if (candidateTokens.has(token)) hits++

    // Jaccard-ish: reward covering the source name without rewarding bloat.
    const score = hits / Math.max(wanted.length, candidateTokens.size)
    if (score > bestScore) {
      bestScore = score
      best = candidate
    }
  }

  // Require a dominant overlap; anything looser produces confident nonsense.
  if (!best || bestScore < 0.6) return null

  return {
    method: 'name',
    aircraftTypeId: best.id,
    confidence: Number(bestScore.toFixed(2)),
    typeLabel: typeLabel(best),
  }
}

function bestDimensionMatch(
  entry: HmCatalogEntry,
  index: AircraftTypeIndex
): AircraftMatch | null {
  const METERS_PER_FOOT = 0.3048
  const wingspanFt = (entry.widthM ?? 0) / METERS_PER_FOOT
  const lengthFt = (entry.lengthM ?? 0) / METERS_PER_FOOT
  if (wingspanFt <= 0 || lengthFt <= 0) return null

  let best: AircraftTypeCandidate | null = null
  let bestError = Infinity

  for (const candidate of index.all) {
    if (!candidate.wingspan || !candidate.length) continue
    const wingspanError = Math.abs(candidate.wingspan - wingspanFt) / wingspanFt
    const lengthError = Math.abs(candidate.length - lengthFt) / lengthFt
    if (wingspanError > DIMS_TOLERANCE || lengthError > DIMS_TOLERANCE) continue

    const error = wingspanError + lengthError
    if (error < bestError) {
      bestError = error
      best = candidate
    }
  }

  if (!best) return null

  return {
    method: 'dims',
    aircraftTypeId: best.id,
    // Perfect dimensional agreement still isn't identity — cap the confidence.
    confidence: Number((0.8 * (1 - bestError / (2 * DIMS_TOLERANCE))).toFixed(2)),
    typeLabel: typeLabel(best),
  }
}
